import { FC, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  MuiBox,
  OptionsDataType,
  WarningConfirmationModal,
} from "../../components";
import {
  getCustomDateFormatWithTimeBasedOnUserFormat,
  PAGE_IDS,
} from "../../helpers/constants";
import { useSiteCore } from "../../hooks/useSiteCore";
import { useToast } from "../../hooks/useToast";
import { toggleLoader } from "../../redux/slices/appState/appStateSlice";
import { SystemMessageSearchForm } from "./components/systemMessageSearchForm/SystemMessageSearchForm";

import {
  SystemMessageSearchFormDefaultValues,
  SystemMessageSearchFormType,
  UserCountry,
} from "./modelsAndSchemas/models";
import { SystemMessageResults } from "./components/SystemMessageSearchResults/SystemMessageSearchResults";
import {
  useDeleteSystemMessageMutation,
  useGetSystemMessageCountryQuery,
  useGetSystemMessageListQuery,
} from "../../api/services/systemMessage/SystemMessageApi";
import { getValueOrBlank, isNotEmpty } from "../../helpers/utils";
import { CreateSystemMessage } from "../createSystemMessage/CreateSystemMessage";
import { ViewSystemMessage } from "../viewSystemMessage/ViewSystemMessage";
import { searchSystemMessageSiteCoreKeys } from "./siteCore/SiteCoreKeys";
import { MessageSearchHeader } from "./components/systemMessageSearchHeader/SystemMessageSearchHeader";
import { createSystemMessageSiteCoreKeys } from "../createSystemMessage/siteCore/SiteCoreKeys";
import { SearchSystemMessageSchema } from "./modelsAndSchemas/ValidationSchema";
import { useGetLoggedInUserDetails } from "../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { getDateFormatByCountry } from "../../helpers/constants/countryDateFormat";

export const pageId = PAGE_IDS.SYSTEM_MESSAGES;

export const SystemMessageSearch: FC = () => {
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );
  const [isSearchClick, setIsSearchClick] = useState(false);
  const [showCreateSuccessModal, setShowCreateSuccessModal] = useState(false);

  const [messageModalState, setMessageModalState] = useState({
    showAdd: false,
    showView: false,
    selectedId: "",
    showEdit: false,
    showDelete: false,
  });
  const { displayToast } = useToast();

  const { commonLabels, fetchLabel, commonValidationMessages } = useSiteCore();
  const dispatch = useDispatch();

  const methods = useForm<SystemMessageSearchFormType>({
    mode: "all",
    defaultValues: SystemMessageSearchFormDefaultValues,
    resolver: yupResolver(SearchSystemMessageSchema(commonValidationMessages)),
  });

  const {
    isFetching: isSearchPending,
    data: searchResult,
    isError: isSearchError,
    refetchWithParams: refetchSearchResults,
  } = useGetSystemMessageListQuery();

  const {
    data: countryList,
    isLoading: isUserCountryListLoading,
    isError: isUserCountryListError,
  } = useGetSystemMessageCountryQuery();

  const {
    isPending: isDeleteMessageLoading,
    isError: isDeleteMessageError,
    isSuccess: isDeleteMessageSuccess,
    mutateAsync: deleteSystemMessage,
  } = useDeleteSystemMessageMutation();

  const { reset, getValues, handleSubmit } = methods;

  const userCountryDateFormatWithTime = useMemo(() => {
    return getCustomDateFormatWithTimeBasedOnUserFormat(userCountryDateFormat);
  }, [userCountryDateFormat]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible:
          isSearchPending || isUserCountryListLoading || isDeleteMessageLoading,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isSearchPending, isUserCountryListLoading, isDeleteMessageLoading]);

  useEffect(() => {
    if (isSearchError || isUserCountryListError || isDeleteMessageError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isSearchError, isUserCountryListError, isDeleteMessageError]);

  useEffect(() => {
    if (isDeleteMessageSuccess) {
      displayToast(
        fetchLabel(
          searchSystemMessageSiteCoreKeys.resultsKey,
          searchSystemMessageSiteCoreKeys.systemMessageDeletedMessage,
        ),
      );
      dispatch(toggleLoader({ isVisible: false }));
      handleCloseAddMessageModal();
      getSearchResults();
    }
  }, [isDeleteMessageSuccess]);

  const onAddMessage = () => {
    setMessageModalState({
      showAdd: true,
      showView: false,
      selectedId: "",
      showEdit: false,
      showDelete: false,
    });
  };

  const handleSearchClick = async () => {
    setIsSearchClick(true);
    getSearchResults();
  };

  const handleClearAllSearch = () => {
    reset(SystemMessageSearchFormDefaultValues);
    setIsSearchClick(false);
  };

  const handleViewMessage = (id: string) => {
    setMessageModalState({
      showAdd: false,
      showView: true,
      selectedId: id,
      showEdit: false,
      showDelete: false,
    });
  };

  const handleEditMessage = (id: string) => {
    setMessageModalState({
      showAdd: false,
      showView: false,
      selectedId: id,
      showEdit: true,
      showDelete: false,
    });
  };

  const handleDeleteMessage = (id: string) => {
    setMessageModalState({
      showAdd: false,
      showView: false,
      selectedId: id,
      showEdit: false,
      showDelete: true,
    });
  };

  const getSearchResults = () => {
    const { country } = getValues();
    refetchSearchResults({ countryCode: country });
  };

  const handleCloseAddMessageModal = () => {
    setMessageModalState({
      showAdd: false,
      showView: false,
      selectedId: "",
      showEdit: false,
      showDelete: false,
    });
  };

  const handleSuccessCreateMessage = () => {
    handleCloseAddMessageModal();
    isSearchClick && getSearchResults();
  };

  const countryListOptions = useMemo(() => {
    let countries: OptionsDataType[] = [];
    if (isNotEmpty(countryList)) {
      const uniqueCountries: UserCountry[] = Array.from(
        new Map(
          countryList.map((country: UserCountry) => [
            country.countryIdentifier,
            country,
          ]),
        ).values(),
      ) as UserCountry[];

      countries = uniqueCountries.map((country: UserCountry) => ({
        label: country.masterCountryName,
        value: country.countryIdentifier,
      }));
    }
    return countries;
  }, [countryList]);

  const handleSuccessConfirm = () => {
    setShowCreateSuccessModal(false);
  };

  return (
    <MuiBox data-testid="users-search">
      <MessageSearchHeader pageId={pageId} onAddMessage={onAddMessage} />

      <FormProvider {...methods}>
        <SystemMessageSearchForm
          pageId={pageId}
          handleSearchClick={handleSubmit(handleSearchClick)}
          handleClearAllSearch={handleClearAllSearch}
          countryListOptions={countryListOptions}
        />
      </FormProvider>

      {isSearchClick && (
        <SystemMessageResults
          pageId={pageId}
          rows={isNotEmpty(searchResult) ? searchResult : []}
          isSearchClick={isSearchClick}
          handleViewMessage={handleViewMessage}
          handleEditMessage={handleEditMessage}
          handleDeleteMessage={handleDeleteMessage}
          userCountryDateFormatWithTime={userCountryDateFormatWithTime}
        />
      )}

      {(messageModalState.showAdd || messageModalState.showEdit) && (
        <CreateSystemMessage
          open={messageModalState.showAdd || messageModalState.showEdit}
          onClose={handleCloseAddMessageModal}
          handleSuccess={handleSuccessCreateMessage}
          isEditMessage={messageModalState.showEdit}
          selectedMessageId={messageModalState.selectedId}
          countryList={countryList}
          setShowCreateSuccessModal={setShowCreateSuccessModal}
          userCountryDateFormatWithTime={userCountryDateFormatWithTime}
        />
      )}
      {messageModalState.showDelete && (
        <WarningConfirmationModal
          show={messageModalState.showDelete}
          modalTitle={commonLabels.confirmation}
          onClose={handleCloseAddMessageModal}
          onPrimaryButtonClick={() =>
            deleteSystemMessage(messageModalState.selectedId)
          }
          labelContent={fetchLabel(
            searchSystemMessageSiteCoreKeys.resultsKey,
            searchSystemMessageSiteCoreKeys.systemMessageDeleteConfirmation,
          )}
          secondaryButtonTitle={commonLabels.no}
        />
      )}
      {messageModalState.showView && (
        <ViewSystemMessage
          open={messageModalState.showView}
          onClose={handleCloseAddMessageModal}
          selectedId={messageModalState.selectedId}
          handleEditMessage={handleEditMessage}
          userCountryDateFormatWithTime={userCountryDateFormatWithTime}
        />
      )}
      {showCreateSuccessModal && (
        <WarningConfirmationModal
          show={showCreateSuccessModal}
          modalTitle={commonLabels.success}
          onClose={handleSuccessConfirm}
          onPrimaryButtonClick={handleSuccessConfirm}
          primaryButtonTitle={commonLabels.ok}
          labelContent={fetchLabel(
            createSystemMessageSiteCoreKeys.createMessageKey,
            createSystemMessageSiteCoreKeys.systemMessagePublishedMessage,
          )}
          showSecondaryButton={false}
        />
      )}
    </MuiBox>
  );
};
